import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import {
  CONNECTION_STATUS,
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
} from '@/atoms/chronicle';
import type { ChronicleError } from '@/tauri/chronicle/types';

/**
 * Hook for managing Chronicle error modal state.
 * Provides utilities for showing, hiding, and clearing errors.
 */
export function useChronicleErrorModal() {
  const [connectionContext, setConnectionContext] = useAtom(
    chronicleConnectionContextAtom
  );
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const deviceState = useAtomValue(chronicleDeviceStateAtom);

  /**
   * Set error state and open error modal.
   */
  const setErrorWithModal = useCallback(
    (error: ChronicleError) => {
      setDeviceState((prev) => {
        // HAR-1910 Soft-PASS: once radio-off owns the modal, a late
        // connect/DeviceSearch DEVICE_NOT_FOUND must not replace it.
        if (
          connectionContext.isErrorModalOpen &&
          prev.error?.code === 'BLUETOOTH_DISABLED' &&
          error.code === 'DEVICE_NOT_FOUND'
        ) {
          return prev;
        }
        return {
          ...prev,
          connectionStatus: CONNECTION_STATUS.ERROR,
          error,
        };
      });
      setConnectionContext((prev) => ({
        ...prev,
        isErrorModalOpen: true,
      }));
    },
    [setDeviceState, setConnectionContext, connectionContext.isErrorModalOpen]
  );

  /**
   * Close the error modal without clearing the error.
   */
  const closeErrorModal = useCallback(() => {
    setConnectionContext((prev) => ({
      ...prev,
      isErrorModalOpen: false,
    }));
  }, [setConnectionContext]);

  /**
   * Clear the error state without closing the modal.
   */
  const clearError = useCallback(() => {
    setDeviceState((prev) => ({
      ...prev,
      error: null,
    }));
  }, [setDeviceState]);

  /**
   * Close the error modal and clear the error state.
   *
   * APP-4895 (Phase C, C1): If `connectionStatus` is `ERROR`, reset it to
   * `DISCONNECTED` so the sidebar tile / recording-page guards don't treat
   * the post-error state as sticky `ERROR`. Leave `Connected` / `Reconnecting`
   * alone — those mean the underlying state moved on while the user idled
   * with the modal open.
   */
  const dismissError = useCallback(() => {
    closeErrorModal();
    setDeviceState((prev) => ({
      ...prev,
      // While Rust-driven reconnect is live, `error` is the carried recovery
      // reason (it renders the inline hint) and Rust won't re-emit it —
      // dismissing the modal must not eat it.
      error: prev.connectionStatus === CONNECTION_STATUS.RECONNECTING ? prev.error : null,
      connectionStatus:
        prev.connectionStatus === CONNECTION_STATUS.ERROR
          ? CONNECTION_STATUS.DISCONNECTED
          : prev.connectionStatus,
    }));
  }, [closeErrorModal, setDeviceState]);

  return {
    isErrorModalOpen: connectionContext.isErrorModalOpen,
    error: deviceState.error,
    setErrorWithModal,
    closeErrorModal,
    clearError,
    dismissError,
  };
}
